import { z } from 'zod';
import { ALL_ADMIN_ROLES } from './enums';

/**
 * Contrato da callable `listAdminUsers` — quem tem acesso ao painel admin
 * (claim `adminRole` ou o legado `superAdmin: true`), lido do Firebase Auth.
 *
 * Origem: #795 — 2026-10-03 14:13 +0200 (ADM-6).
 *
 * Por quê: a tela `/settings/super-admins` reconstruía a lista a partir das
 * últimas 100 entradas de `/audit` (de TODAS as ações). Quem foi concedido há
 * mais de 100 eventos sumia da lista — e quem não aparece não pode ser
 * revogado; concessões por `setAdminRole` (`ADMIN_ROLE_*`) nem eram lidas. O
 * Auth é a fonte da verdade do claim (CLAUDE.md, "Auth/identidade"), e só o
 * servidor pode listá-lo.
 *
 * Dados — resposta (exemplo anonimizado):
 *   {
 *     "admins": [{
 *       "uid": "uid-1", "email": "fulano@etiquetachef.com",
 *       "role": "ROOT",             // papel EFETIVO (resolveAdminRole)
 *       "legacyClaim": true,        // só `superAdmin: true`, sem `adminRole`
 *       "disabled": false, "emailVerified": true,
 *       "lastSignInAtMillis": 1790000000000,  // null = nunca entrou
 *       "createdAtMillis": 1780000000000
 *     }]
 *   }
 *
 * Usado por: `apps/functions/src/admin/listAdminUsers.ts` (produz),
 * `apps/admin/src/features/superAdmins/useAdminUsers.ts` (consome).
 */
export const ListAdminUsersInput = z.object({}).strict();
export type ListAdminUsersInput = z.infer<typeof ListAdminUsersInput>;

export const AdminUserRowSchema = z.object({
  uid: z.string().min(1),
  /** `null` em conta sem e-mail (não deveria existir para admin; aparece para ser revogada). */
  email: z.string().nullable(),
  /** Papel efetivo: `adminRole` válido, senão `superAdmin: true` → `ROOT`. */
  role: z.enum(ALL_ADMIN_ROLES),
  /** `true` quando o acesso vem só do claim legado `superAdmin` (sem `adminRole`). */
  legacyClaim: z.boolean(),
  /** Conta bloqueada no Firebase Auth. */
  disabled: z.boolean(),
  /** `emailVerified` do Auth — admin sem isso não passa em `requireAdminRole` (#795, ADM-3). */
  emailVerified: z.boolean(),
  lastSignInAtMillis: z.number().int().nonnegative().nullable(),
  createdAtMillis: z.number().int().nonnegative().nullable(),
});
export type AdminUserRow = z.infer<typeof AdminUserRowSchema>;

export const ListAdminUsersResultSchema = z.object({
  admins: z.array(AdminUserRowSchema),
});
export type ListAdminUsersResult = z.infer<typeof ListAdminUsersResultSchema>;
